// Which translation namespaces a first paint needs, per kind of page.
//
// vite.config.ts packs the JSON files of each language into one chunk per group
// ("i18n-<lng>-<group>"), so a visitor of the public booking page downloads the small
// "shell" chunk instead of every namespace of the language. Namespaces that a component
// declares via useTranslation() but that are not listed here are still fetched on demand
// (the component suspends until they arrive); the lists below only decide what is fetched
// up front, so a page that reads a namespace it never declares must have it listed.
// Pages not covered by a narrow list (admin, staff, guest, pricing, ...) load every namespace.

export type NamespaceGroup = string; // 'shell' | 'site' | 'plans' | 'app'

/** Needed by every public page: app shell, cookie banner, language pill, booking page, login. */
export const SHELL_NAMESPACES = ['common', 'validation', 'legal', 'login', 'auth', 'header', 'footer'];

/** Shared by the marketing / landing pages. */
export const SITE_NAMESPACES = ['home', 'about', 'blog'];

/** Pricing / plan copy: used by the marketing pages and by the admin billing screens. */
export const PLANS_NAMESPACES = ['plans', 'subscription', 'registration'];

/** Privacy policy, terms and cookie policy copy: only the legal pages read it (the cookie banner uses "legal", in the shell). */
export const LEGAL_PAGES_NAMESPACE = 'legalPages';

export function namespaceGroup(namespace: string): NamespaceGroup {
  if (SHELL_NAMESPACES.includes(namespace)) return 'shell';
  if (SITE_NAMESPACES.includes(namespace)) return 'site';
  if (PLANS_NAMESPACES.includes(namespace)) return 'plans';
  if (namespace === LEGAL_PAGES_NAMESPACE) return 'legal-pages';
  return 'app';
}

/** The public quote / invoice pages opened from a link. They carry their own strings (see features/clientPages) and need no namespace. */
export const CLIENT_PAGE_PATH = /^\/(q|i|request)\/[^/]+\/?$/;

const BOOKING_PATH = /^\/(book|[^/]+\/book)\/?$/;
const APP_PATH = /^\/(admin|staff)(\/|$)/;
const MARKETING_PATH =
  /^\/(about|blog(\/.*)?|(legal\/)?(privacy|terms|cookies))\/?$/;
const LEGAL_PATH = /^\/(legal\/)?(privacy|terms|cookies)\/?$/;

/** Namespaces to load before first render for the page being opened. */
export function initialNamespaces(
  allNamespaces: string[],
  pathname: string,
  isSubdomainRoot: boolean,
): string[] {
  if (CLIENT_PAGE_PATH.test(pathname)) return [];
  if (BOOKING_PATH.test(pathname) || (pathname === '/' && isSubdomainRoot) || pathname === '/login') {
    return SHELL_NAMESPACES;
  }
  if (pathname === '/' || MARKETING_PATH.test(pathname)) {
    const legal = LEGAL_PATH.test(pathname) ? [LEGAL_PAGES_NAMESPACE] : [];
    return [...SHELL_NAMESPACES, ...SITE_NAMESPACES, ...PLANS_NAMESPACES, ...legal];
  }
  if (APP_PATH.test(pathname)) {
    // Everything except the marketing copy (home/about/blog) and the legal pages.
    return allNamespaces.filter((ns) => !SITE_NAMESPACES.includes(ns) && ns !== LEGAL_PAGES_NAMESPACE);
  }
  return allNamespaces;
}
